import { useState } from 'react';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Kanban,
  List,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCompanies,
  useUpdateCompany,
  useDeleteCompany,
} from '@/modules/core/companies/hooks/useCompanies';
import { CompaniesFilters } from '@/modules/core/companies/components/CompaniesFilters';
import type {
  Company,
  CompanyFilters,
} from '@/modules/core/companies/types/company.types';
import { CompaniesTable } from '../components/companies/CompaniesTable';
import { CompaniesKanban } from '../components/companies/CompaniesKanban';
import { CompanyAddDialog } from '../components/companies/CompanyAddDialog';

type ViewMode = 'list' | 'kanban';

const PAGE_SIZES = [10, 20, 50];

export function CompaniesPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [filters, setFilters] = useState<CompanyFilters>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const [convertTarget, setConvertTarget] = useState<Company | null>(null);

  const kanbanPagination = viewMode === 'kanban'
    ? { page: 1, limit: 200, sortBy: 'name', sortOrder: 'asc' as const }
    : { page, limit, sortBy, sortOrder };

  const { data, isLoading, error } = useCompanies(filters, kanbanPagination);

  const updateCompany = useUpdateCompany();
  const deleteCompany = useDeleteCompany();

  const companies = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = data?.meta.totalPages ?? 1;

  const prospectCount = companies.filter((c) => c.companyType === 'prospect').length;
  const clientCount = companies.filter((c) => c.companyType === 'client').length;
  const totalOpportunities = companies.reduce(
    (sum, c) => sum + (c.opportunitiesCount ?? 0),
    0
  );

  const handleFiltersChange = (next: CompanyFilters) => {
    setFilters(next);
    setPage(1);
  };

  const handleConvertToClient = () => {
    if (!convertTarget) return;
    updateCompany.mutate(
      { id: convertTarget.id, data: { companyType: 'client' } },
      {
        onSuccess: () => {
          toast.success(`${convertTarget.name} passée en client`);
          setConvertTarget(null);
        },
        onError: () => toast.error('Erreur lors du changement de type'),
      }
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteCompany.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Société supprimée');
        setDeleteTarget(null);
      },
      onError: () => toast.error('Erreur lors de la suppression'),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-copper" />
            Sociétés
          </h1>
          <p className="text-muted-foreground">
            Prospects et clients du module Tenders Management
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-2"
            >
              <List className="h-4 w-4" />
              Liste
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className="gap-2"
            >
              <Kanban className="h-4 w-4" />
              Kanban
            </Button>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle société
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Prospects (page)</p>
            <p className="text-2xl font-bold text-amber-700">{prospectCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Clients (page)</p>
            <p className="text-2xl font-bold text-green-700">{clientCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Opp. liées (page)</p>
            <p className="text-2xl font-bold text-copper">{totalOpportunities}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <CompaniesFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {/* Erreur */}
      {error && (
        <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
          Une erreur est survenue lors du chargement des sociétés.
        </div>
      )}

      {/* Vue Liste */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {/* Tri */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {total > 0
                ? `${total} société${total > 1 ? 's' : ''}`
                : 'Aucune société'}
            </p>
            <div className="flex items-center gap-2">
              <Select
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nom</SelectItem>
                  <SelectItem value="city">Ville</SelectItem>
                  <SelectItem value="createdAt">Date d'ajout</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={sortOrder}
                onValueChange={(value) => {
                  setSortOrder(value as 'asc' | 'desc');
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Croissant</SelectItem>
                  <SelectItem value="desc">Décroissant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <CompaniesTable
            companies={companies}
            isLoading={isLoading}
            onDelete={setDeleteTarget}
            onConvertToClient={setConvertTarget}
          />

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Afficher</span>
                <Select
                  value={String(limit)}
                  onValueChange={(value) => {
                    setLimit(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">par page</span>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-4 text-sm">
                  Page {page} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vue Kanban */}
      {viewMode === 'kanban' && (
        <CompaniesKanban
          companies={companies}
          isLoading={isLoading}
          onConvertToClient={setConvertTarget}
        />
      )}

      {/* Dialog ajout */}
      <CompanyAddDialog open={addOpen} onOpenChange={setAddOpen} />

      {/* Dialog confirmation conversion */}
      <AlertDialog
        open={convertTarget !== null}
        onOpenChange={(open) => !open && setConvertTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Passer en client ?</AlertDialogTitle>
            <AlertDialogDescription>
              "{convertTarget?.name}" sera marquée comme client. Cette action
              peut être annulée en modifiant la fiche.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertToClient}>
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog confirmation suppression */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la société ?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" sera supprimée (soft delete). Les
              opportunités associées ne seront pas affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
